import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faApple, faGooglePlay } from '@fortawesome/free-brands-svg-icons';
import { faArrowUpRightFromSquare, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import Reveal from './Reveal';
import Eyebrow from './Eyebrow';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { caseStudies, experiments } from '../data/content';

const PRIMARY_FIELDS = [
  ['problem', 'Problem'],
  ['idea', 'Idea'],
];

const SECONDARY_FIELDS = [
  ['approach', 'Approach'],
  ['built', 'What I built'],
  ['challenge', 'Challenge'],
  ['impact', 'Impact'],
  ['learned', 'What I learned'],
];

const STATUS_VARIANT = {
  Production: 'success',
  'In Progress': 'warning',
  'Seeding Users': 'peach',
};

const StatusBadge = ({ status }) => (
  <Badge variant={STATUS_VARIANT[status] || 'default'} dot>
    {status}
  </Badge>
);

const Field = ({ label, value }) => (
  <div>
    <dt className="mb-1.5 font-mono text-[0.7rem] uppercase tracking-wide text-faint">{label}</dt>
    <dd className="max-w-[65ch] text-[1rem] leading-relaxed text-muted">{value}</dd>
  </div>
);

const Projects = () => {
  const [activeId, setActiveId] = useState(caseStudies[0].id);
  const [expanded, setExpanded] = useState(false);
  const active = caseStudies.find((c) => c.id === activeId);

  useEffect(() => {
    setExpanded(false);
  }, [activeId]);

  return (
    <section id="projects" className="scroll-mt-20 snap-start py-24 md:py-32">
      <div className="container">
        <Reveal>
          <Eyebrow>03 — Projects</Eyebrow>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="max-w-2xl text-[clamp(2rem,3.6vw,3rem)] font-bold tracking-tight">
            A few things I've built, told the way I actually think about them.
          </h2>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-[0.85fr_1.4fr] lg:items-start">
          <div className="flex gap-2 overflow-x-auto pb-2 lg:sticky lg:top-28 lg:flex-col lg:overflow-visible lg:pb-0">
            {caseStudies.map((cs) => {
              const isActive = activeId === cs.id;
              return (
                <button
                  key={cs.id}
                  onClick={() => setActiveId(cs.id)}
                  className={cn(
                    'w-64 shrink-0 rounded-xl border border-border p-4 text-left transition-colors lg:w-auto lg:shrink lg:rounded-none lg:border-0 lg:border-b lg:border-l-2 lg:p-4',
                    isActive
                      ? 'bg-primary/5 lg:border-b-border lg:border-l-primary'
                      : 'hover:bg-white/[0.02] lg:border-b-border lg:border-l-transparent'
                  )}
                >
                  <div className="mb-2 flex items-center gap-2">
                    <span className="font-mono text-xs text-faint">{cs.number}</span>
                    <StatusBadge status={cs.status} />
                  </div>
                  <div className="font-display text-base font-semibold leading-snug text-foreground">{cs.name}</div>
                  <p className="mt-1.5 text-sm font-semibold text-foreground/90">{cs.highlight}</p>
                  <p className="mt-1 text-sm text-faint">{cs.tagline}</p>
                </button>
              );
            })}
          </div>

          <Card className="min-h-[520px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                className="p-6 md:p-8"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge status={active.status} />
                  <Badge variant="primary">{active.role}</Badge>
                </div>

                <h3 className="mt-5 text-[clamp(1.35rem,2.2vw,1.85rem)] font-bold leading-tight tracking-tight">{active.name}</h3>
                <p className="mt-2 max-w-[52ch] text-[1.05rem] text-muted">{active.tagline}</p>

                <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-border pt-5">
                  <span className="mr-1 font-mono text-[0.7rem] uppercase tracking-wide text-faint">Stack</span>
                  {active.tags.map((t) => (
                    <Badge variant="tag" key={t}>
                      {t}
                    </Badge>
                  ))}
                </div>

                <dl className="mt-8 space-y-6">
                  {PRIMARY_FIELDS.map(([key, label]) => (
                    <Field key={key} label={label} value={active[key]} />
                  ))}
                </dl>

                <AnimatePresence initial={false}>
                  {expanded && (
                    <motion.dl
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                      className="space-y-6 overflow-hidden"
                    >
                      <div className="pt-6">
                        {SECONDARY_FIELDS.map(([key, label], i) => (
                          <div className={i > 0 ? 'mt-6' : ''} key={key}>
                            <Field label={label} value={active[key]} />
                          </div>
                        ))}
                      </div>
                    </motion.dl>
                  )}
                </AnimatePresence>

                <button
                  onClick={() => setExpanded((v) => !v)}
                  className="mt-6 flex items-center gap-2 font-mono text-[0.8rem] text-primary-light"
                >
                  {expanded ? 'Show less' : 'See more'}
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className={cn('text-xs transition-transform duration-200', expanded && 'rotate-180')}
                  />
                </button>

                {(active.appStoreUrl || active.playStoreUrl) && (
                  <div className="mt-8 flex flex-wrap gap-3 border-t border-border pt-6">
                    {active.appStoreUrl && (
                      <Button asChild variant="outline">
                        <a href={active.appStoreUrl} target="_blank" rel="noopener noreferrer">
                          <FontAwesomeIcon icon={faApple} /> App Store
                        </a>
                      </Button>
                    )}
                    {active.playStoreUrl && (
                      <Button asChild variant="outline">
                        <a href={active.playStoreUrl} target="_blank" rel="noopener noreferrer">
                          <FontAwesomeIcon icon={faGooglePlay} /> Google Play
                        </a>
                      </Button>
                    )}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </Card>
        </div>

        <Reveal delay={0.1} className="mt-24 border-t border-border pt-16">
          <h3 className="text-2xl font-bold">Also in the workshop</h3>
          <p className="mt-2 max-w-[60ch] text-muted">
            Smaller builds that didn't need the full case-study treatment — but taught me something anyway.
          </p>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {experiments.map((exp) => (
              <a
                key={exp.id}
                href={exp.githubLink}
                target={exp.githubLink !== '#' ? '_blank' : undefined}
                rel="noopener noreferrer"
                className={cn(
                  'group rounded-2xl border border-border bg-surface p-5 transition-all',
                  exp.githubLink !== '#' && 'hover:-translate-y-1 hover:border-border-strong'
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h4 className="font-semibold leading-snug text-accent-lavender">{exp.title}</h4>
                    {exp.org && <p className="mt-0.5 text-sm text-foreground/80">{exp.org}</p>}
                  </div>
                  {exp.githubLink !== '#' && (
                    <FontAwesomeIcon
                      icon={faArrowUpRightFromSquare}
                      className="mt-1 shrink-0 text-xs text-faint transition-colors group-hover:text-primary-light"
                    />
                  )}
                </div>
                <span className="mt-2 block font-mono text-[0.72rem] text-muted">{exp.stack}</span>
                <p className="mt-3 text-sm leading-relaxed text-muted">{exp.note}</p>
              </a>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Projects;
